<script>
import { h } from 'vue';
import DolCard from './dol-card';
import DolDict from './dol-dict.vue';
import DolSelect from '../form/dol-select.vue';
import DolImagePreview from './dol-image-preview.vue';
import DolListColumnSetting from './dol-list-column-setting.vue';
import { Table, Tag, Input, Image, InputNumber, Pagination, Spin } from 'ant-design-vue';

import requestMixins from '../common/requestMixins';

const COMMON_CELL_RENDER = (options, $vue) => {
  const { value, column, record, index } = options;
  let render;
  switch (column.fieldType) {
    case 'rowIndex':
      render = h('span', index + 1);
      break;
    case 'boolean':
      render = h('span', typeof value === 'boolean' ? (value ? (column.trueText || '是') : (column.falseText || '否')) : (column.nullText || ''));
      break;
    case 'dict':
      render = value && h(DolDict, {
        dictCode: column.dictCode,
        value: value,
        tag: column.tag,
        color: column.tagColor
      });
      break;
    case 'ref':
      render = h('span', value && $vue.$dt(value, column.labelField || 'name'));
      break;
    case 'refArray':
      render = h('span', value ? value.map(item => $vue.$dt(item, column.labelField || 'name')).join(column.separator || ', ') : '');
      break;
    case 'tagArray':
      render = tagArrayRender(options, $vue);
      break;
    case 'pre':
      render = h('pre', { style: { 'white-space': 'pre-wrap' } }, value);
      break;
    case 'html':
      render = h({ template: value });
      break;
    case 'wordBreakAll':
      render = h('span', { class: 'word-break-all' }, value);
      break;
    case 'object':
      render = h('div', value ? Object.keys(value).map(key => h('div', `${key} = ${value[key]}`)) : '');
      break;
    case 'editInput':
      render = h(Input, {
        value,
        'onUpdate:value': v => record[column.dataIndex] = v,
        onChange: (v, o) => typeof column.onChange === 'function' && column.onChange(v, o, record),
        style: { width: '100%' }
      });
      break;
    case 'editSelect':
      render = h(DolSelect, {
        value,
        'onUpdate:value': v => record[column.dataIndex] = v, ...column.editOption,
        onChange: (v, o) => typeof column.onChange === 'function' && column.onChange(v, o, record),
        style: { width: '100%' }
      });
      break;
    case 'editNumber':
      render = h(InputNumber, {
        value,
        'onUpdate:value': v => record[column.dataIndex] = v,
        onChange: (v, o) => typeof column.onChange === 'function' && column.onChange(v, o, record),
        style: { width: '100%' }
      });
      break;
    case 'image':
      render = value && h(DolImagePreview, { imageList: [value] });
      break;
    case 'imageList':
      render = value && h(DolImagePreview, { imageList: value });
      break;
    case 'params':
      render = paramsRender(options);
      break;
    case 'activeScope':
      const active = record[column.activeField || 'activeDate'] || '不限';
      const expired = record[column.expiredField || 'expiredDate'] || '不限';
      render = `${active} ~ ${expired}`;
      break;
  }
  return render;
};
const COMMON_HEADER_RENDER = (options, columns, showListFields, $emit, columnsSetting) => {
  const { column } = options;
  let render;
  switch (column.dataIndex) {
    case 'operation':
      if (columnsSetting) {
        render = h(DolListColumnSetting, { fields: columns, showListFields, 'onUpdate:show-list-fields': v => $emit('update:show-list-fields', v) });
      }
      break;
  }
  return render;
};

function tagArrayRender({ value, column, record, index }, $vue) {
  let tagList = [];
  if (value && value.length > 0) {
    value.forEach(item => {
      tagList.push(h(Tag, () => ($vue.$dt(item, column.labelField || 'name'))));
    });
  }
  return h('div', tagList);
}

function paramsRender({ value, column, record, index }) {
  let params = [];
  if (value && value.length > 0) {
    value.forEach(item => {
      params.push(h('div', `${item[column.keyField || 'key']} = ${item[column.valueField || 'value']}`));
    });
  }
  return h('div', params);
}

export default {
  name: 'dol-list',
  mixins: [requestMixins],
  emits: ['row-click', 'update:show-list-fields', 'update:selectedRowKeys', 'update:selectedRows', 'onSelectChange'],
  props: {
    cardFlag: { type: Boolean, default: true },
    cardClass: { type: String },
    cardSize: { type: String, default: 'normal' },
    title: { type: String, default: '列表' },
    titleFlag: { type: Boolean, default: true },

    columns: { type: Array, default: () => [] },  //列信息配置
    showListFields: { type: Object, default: () => ({}) },
    columnsSetting: { type: Boolean, default: true },

    rowIndex: { type: Boolean, default: true },
    rowIndexFixed: { type: Boolean, default: false },
    size: { type: String, default: 'small' },
    rowKey: { type: String, default: '_id' },
    bordered: { type: Boolean, default: false },

    selectedRowKeys: { type: Array, default: null },
    selectedRows: { type: Array, default: null },
    selectionGetCheckboxProps: { type: Function, default: null },
    selectionType: { type: String, default: null },

    defaultPageSize: { type: Number, default: 10 },                                        //每页条数

    scrollX: { default: null },
    scrollY: { default: '100%' },
    scroll: { type: Object },
  },
  computed: {
    innerColumns() {
      // 过滤出需要显示的字段
      const innerColumns = this.columns.filter(item => {
        if (item.listHidden) return false;
        if (typeof this.showListFields?.[item.dataIndex] === 'boolean') return this.showListFields[item.dataIndex];
        return typeof item.listFilter === 'undefined' || item.listFilter;
      });
      if (this.rowIndex) {
        const rowIndexColumn = { dataIndex: '#', title: '#', align: 'center', width: '40px', fieldType: 'rowIndex' };
        if (this.rowIndexFixed) {
          rowIndexColumn.fixed = 'left';
        }
        innerColumns.unshift(rowIndexColumn);
      }
      return innerColumns.map(item => {
        if (item.dataIndex === 'operation' && !item.align) item.align = 'right';
        return item;
      });
    },
    paginationParam() {
      if (this.pagination) {
        let params = {
          total: this.innerTotal,
          showSizeChanger: this.paginationShowSizeChanger,
          pageSizeOptions: this.pageSizeOption,
          pageSize: this.pageSize,
          current: this.pageNumber,
          showQuickJumper: this.paginationShowQuickJumper,
          size: 'default',
        };
        if (this.paginationShowTotal) {
          params.showTotal = (total, range) => this.$t('第x-y条，共n条', { x: range[0], y: range[1], n: total });
        }
        return params;
      } else {
        return false;
      }
    },
  },
  methods: {
    onSelectChange(selectedRowKeys, selectedRows) {
      this.$emit('update:selectedRowKeys', selectedRowKeys);
      this.$emit('update:selectedRows', selectedRows);
      this.$emit('onSelectChange', selectedRowKeys, selectedRows);
    },
    onPageChange(pagination, filters, sorter) {
      this.pageSize = pagination.pageSize;
      this.pageNumber = pagination.current;

      if (this.paginationType === 'server') {
        if (sorter) {
          this.sorter = sorter;
        }
        this.load();
      }
    },
    query(reload) {
      if (reload) this.pageNumber = this.defaultPageNumber;
      return this.load();
    },
  },
  created() {
    if (this.source === 'server' && this.initDataFlag) {
      this.query();
    }
  },
  render() {
    const cardProps = {
      class: `dolphin-list ${this.cardClass}`,
      title: this.title,
      titleFlag: this.titleFlag,
      cardFlag: this.cardFlag,
      size: this.cardSize,
    };

    if (this.$slots.customerContent) {
      const customerContentParams = {
        data: this.innerData || [],
        total: this.innerTotal || 0,
      }
      const children = [];
      children.push(
        h('div', { class: 'dol-list-customer-content-data', style: { position: 'relative' } },
          [
            h('div', { class: 'loading-div', style: { display: this.loadingFlag ? 'flex' : 'none' } }, h(Spin)),
            this.$slots.customerContent(customerContentParams)
          ])
      );
      if (this.pagination) {
        children.push(h(Pagination, {
          class: 'dol-list-customer-content-pagination', ...this.paginationParam,
          onChange: (page, pageSize) => this.onPageChange({ current: page, pageSize })
        }));
      }
      return h(DolCard, cardProps, {
        extra: this.$slots.extra,
        queryForm: this.$slots.queryForm,
        default: () => h('div', { class: 'dol-list-customer-content' }, children),
      })
    } else {
      const tableProps = {
        columns: this.innerColumns,
        dataSource: this.innerData,

        pagination: this.paginationParam,

        size: this.size,
        rowKey: this.rowKey,
        bordered: this.bordered,

        loading: this.loadingFlag,

        customRow: record => ({ onClick: event => this.$emit('row-click', { event, record }) }),

        onChange: this.onPageChange,
      };

      if (this.selectedRowKeys) {
        tableProps.rowSelection = {
          selectedRowKeys: this.selectedRowKeys,
          onChange: this.onSelectChange,
        };
        if (this.selectionGetCheckboxProps) {
          tableProps.rowSelection.getCheckboxProps = this.selectionGetCheckboxProps;
        }
        if (this.selectionType) {
          tableProps.rowSelection.type = this.selectionType;
        }
      }

      if (this.scroll) {
        tableProps.scroll = this.scroll;
      } else if (this.scrollX || this.scrollY) {
        tableProps.scroll = {};
        if (this.scrollX) tableProps.scroll.x = this.scrollX;
        if (this.scrollY) tableProps.scroll.y = this.scrollY;
      }

      return h(DolCard, cardProps, {
        title: this.$slots.title,
        extra: this.$slots.extra,
        queryForm: this.$slots.queryForm,
        default: () => h(Table, tableProps, {
          headerCell: params => COMMON_HEADER_RENDER(params, this.columns, this.showListFields, this.$emit, this.columnsSetting) ||
            (this.$slots.headerCell && this.$slots.headerCell(params)),
          bodyCell: params => COMMON_CELL_RENDER(params, this) || (this.$slots.bodyCell && this.$slots.bodyCell(params)),
          summary: params => (this.$slots.summary && this.$slots.summary(params)),
        }),
      });

    }
  },
};
</script>

<style lang="scss">
</style>
